/**
 * Renderer-side operation model.
 *
 * Adapted from `analytics/src/core/creditSessionsOperations`. The shape mirrors
 * what `toLegacyOperations` (in `@gearbox-protocol/sdk/history`) emits, which
 * intentionally matches the legacy charts-server "credit session operation"
 * payload that the analytics frontend has rendered for years.
 *
 * Each subclass implements `getDetails(underlying, tokens)` which the table
 * uses to render rows.
 */
import {
  formatBN,
  LEVERAGE_DECIMALS,
  shortAddress,
} from "@gearbox-protocol/sdk/onchain";
import { format } from "date-fns";
import type { Address } from "viem";

type BigNumberish = bigint | number | string;

export interface TokenLike {
  addr?: Address;
  symbol?: string;
  title?: string;
  decimals: number;
}

export type TokensList = Record<Address, TokenLike>;

const toBigInt = (v: BigNumberish | undefined | null): bigint => {
  if (v == null) return 0n;
  if (typeof v === "bigint") return v;
  if (typeof v === "number") return BigInt(Math.trunc(v));
  return BigInt(v);
};

export interface DetailsEntry {
  token?: Address;
  account?: Address;
  description?: string;
}

export interface OperationDetails {
  from: DetailsEntry[];
  leverage?: string;
  to?: DetailsEntry[];
}

export interface BasePayload {
  protocol?: string;
  txHash?: string;
  blockNum?: number;
  timestamp?: number;
  sessionId?: string;
}

/**
 * Mirrors `LegacyApiOperation` from `@gearbox-protocol/sdk/history` but with
 * the legacy charts-server fields the renderer expects marked optional, since
 * inner-call payloads emitted by `toLegacyOperations` don't all carry them.
 * See {@link injectMissing} for how parent-context fields are propagated.
 */
export interface LegacyApiOperation extends BasePayload {
  operation: string;
  [key: string]: unknown;
}

const operationLabels: Record<string, string> = {
  OpenCreditAccount: "Open Credit Account",
  CloseCreditAccount: "Close Credit Account",
  LiquidateCreditAccount: "Liquidate",
  PartiallyLiquidateCreditAccount: "Partial Liquidate",
  IncreaseBorrowedAmount: "Borrow",
  DecreaseBorrowedAmount: "Repay",
  IncreaseDebt: "Increase Debt",
  DecreaseDebt: "Decrease Debt",
  AddCollateral: "Add Collateral",
  WithdrawCollateral: "Withdraw Collateral",
  UpdateQuota: "Update Quota",
  DirectTokenTransfer: "Transfer",
  MultiCall: "MultiCall",
  BotMulticall: "Bot Multicall",
  Swap: "Swap",
  UniswapSwap: "Uniswap Swap",
  BalancerSwap: "Balancer Swap",
  CurveExchange: "Curve Exchange",
  CurveAddLiquidity: "Curve Add Liquidity",
  CurveRemoveLiquidity: "Curve Remove Liquidity",
  CurveRemoveLiquidityOneCoin: "Curve Remove Liquidity (One Coin)",
  CurveClaims: "Curve Claims",
  CurveWithdrawal: "Curve Withdrawal",
  ConvexDeposit: "Convex Deposit",
  ConvexDepositAndStake: "Convex Deposit & Stake",
  ConvexWithdraw: "Convex Withdraw",
  ConvexWithdrawAndClaim: "Convex Withdraw & Claim",
  ConvexStake: "Convex Stake",
  GetReward: "Get Reward",
  WstETHWrap: "wstETH Wrap",
  WstETHUnwrap: "wstETH Unwrap",
  LidoSubmit: "Lido Submit",
  VaultDeposit: "Vault Deposit",
  MakerDeposit: "Maker Deposit",
  MakerRedeem: "Maker Redeem",
  YearnDeposit: "Yearn Deposit",
  YearnWithDrawn: "Yearn Withdrawn",
};

const labelFor = (op: string): string => operationLabels[op] ?? op;

export abstract class CreditAccountOperation {
  readonly txHash: string;
  readonly date: string;
  readonly time: string;
  readonly timestamp: number;
  readonly protocol: string;
  readonly sessionId: string;
  readonly blockNum: number;
  readonly operation: string;
  readonly operationType: string;

  constructor(opts: BasePayload, operationType: string) {
    this.txHash = opts.txHash ?? "";
    this.protocol = opts.protocol ?? "";
    this.sessionId = opts.sessionId ?? "";
    this.blockNum = opts.blockNum ?? 0;
    this.timestamp = opts.timestamp ?? 0;
    this.operationType = operationType;
    this.operation = labelFor(operationType);
    const d = new Date(this.timestamp * 1000);
    this.date = format(d, "dd.MM.yyyy");
    this.time = format(d, "HH:mm");
  }

  public abstract getDetails(
    underlying: Address,
    tokens: TokensList,
  ): [OperationDetails, CreditAccountOperation[]?];
}

export const tokenAmountDetails = (
  token: Address,
  amount: bigint,
  tokens: TokensList,
): DetailsEntry => {
  const decimals = tokens[token.toLowerCase() as Address]?.decimals ?? 18;
  return { token, description: formatBN(amount, decimals) };
};

export const tokenDetails = (token: Address): DetailsEntry => ({ token });

export const accountDetails = (account: Address): DetailsEntry => ({
  account,
  description: shortAddress(account),
});

class AmountEvent extends CreditAccountOperation {
  readonly amount: bigint;
  readonly token: Address;
  constructor(p: LegacyApiOperation) {
    super(p, p.operation);
    this.amount = toBigInt(p.amount as BigNumberish);
    this.token = (p.token as Address).toLowerCase() as Address;
  }
  getDetails(_: Address, tokens: TokensList): [OperationDetails] {
    return [{ from: [tokenAmountDetails(this.token, this.amount, tokens)] }];
  }
}

class WithdrawCollateralEvent extends CreditAccountOperation {
  readonly amount: bigint;
  readonly token: Address;
  readonly to?: Address;
  readonly phantomToken?: Address;
  constructor(p: LegacyApiOperation) {
    super(p, p.operation);
    this.amount = toBigInt(p.amount as BigNumberish);
    this.token = (p.token as Address).toLowerCase() as Address;
    this.to = p.to as Address | undefined;
    this.phantomToken = p.phantomToken as Address | undefined;
  }
  getDetails(_: Address, tokens: TokensList): [OperationDetails] {
    const from: DetailsEntry[] = [
      tokenAmountDetails(this.token, this.amount, tokens),
    ];
    if (this.phantomToken) {
      from.push({
        token: this.phantomToken,
        description: "phantom token",
      });
    }
    return [
      {
        from,
        ...(this.to ? { to: [accountDetails(this.to)] } : {}),
      },
    ];
  }
}

class TransactionEvent extends CreditAccountOperation {
  readonly from: Address;
  readonly fromAmount: bigint;
  readonly to: Address;
  readonly toAmount: bigint;
  constructor(p: LegacyApiOperation) {
    super(p, p.operation);
    this.from = (p.from as Address).toLowerCase() as Address;
    this.fromAmount = toBigInt(p.fromAmount as BigNumberish);
    this.to = (p.to as Address).toLowerCase() as Address;
    this.toAmount = toBigInt(p.toAmount as BigNumberish);
  }
  getDetails(_: Address, tokens: TokensList): [OperationDetails] {
    return [
      {
        from: [tokenAmountDetails(this.from, this.fromAmount, tokens)],
        to: [tokenAmountDetails(this.to, this.toAmount, tokens)],
      },
    ];
  }
}

class ChangeBorrowedAmount extends CreditAccountOperation {
  readonly amount: bigint;
  constructor(p: LegacyApiOperation) {
    super(p, p.operation);
    this.amount = toBigInt(p.amount as BigNumberish);
  }
  getDetails(underlying: Address, tokens: TokensList): [OperationDetails] {
    return [
      {
        from: [
          tokenAmountDetails(
            underlying.toLowerCase() as Address,
            this.amount,
            tokens,
          ),
        ],
      },
    ];
  }
}

class UpdateQuotaEvent extends CreditAccountOperation {
  readonly token: Address;
  readonly change: bigint;
  constructor(p: LegacyApiOperation) {
    super(p, p.operation);
    this.token = (p.token as Address).toLowerCase() as Address;
    this.change = toBigInt(p.change as BigNumberish);
  }
  getDetails(underlying: Address, tokens: TokensList): [OperationDetails] {
    return [
      {
        from: [
          tokenAmountDetails(
            underlying.toLowerCase() as Address,
            this.change,
            tokens,
          ),
        ],
        to: [tokenDetails(this.token)],
      },
    ];
  }
}

class DirectTokenTransferEvent extends CreditAccountOperation {
  readonly from: Address;
  readonly fromAmount: bigint;
  readonly oldOwner: Address;
  readonly newOwner: Address;
  constructor(p: LegacyApiOperation) {
    super({ ...p, protocol: (p.token as string) ?? p.protocol }, p.operation);
    this.fromAmount = toBigInt(p.amount as BigNumberish);
    this.from = (p.token as Address).toLowerCase() as Address;
    this.oldOwner = p.from as Address;
    this.newOwner = p.to as Address;
  }
  getDetails(_: Address, tokens: TokensList): [OperationDetails] {
    return [
      {
        from: [tokenAmountDetails(this.from, this.fromAmount, tokens)],
        to: [accountDetails(this.newOwner)],
      },
    ];
  }
}

class PartialLiquidateEvent extends CreditAccountOperation {
  readonly token: Address;
  readonly repaidAmount: bigint;
  readonly minSeizedAmount: bigint;
  readonly to?: Address;
  constructor(p: LegacyApiOperation) {
    super(p, p.operation);
    this.token = (p.token as Address).toLowerCase() as Address;
    this.repaidAmount = toBigInt(p.repaidAmount as BigNumberish);
    this.minSeizedAmount = toBigInt(p.minSeizedAmount as BigNumberish);
    this.to = p.to as Address | undefined;
  }
  getDetails(underlying: Address, tokens: TokensList): [OperationDetails] {
    const u = underlying.toLowerCase() as Address;
    return [
      {
        from: [tokenAmountDetails(u, this.repaidAmount, tokens)],
        to: [tokenAmountDetails(this.token, this.minSeizedAmount, tokens)],
      },
    ];
  }
}

class MultiCallEvent extends CreditAccountOperation {
  readonly multicall: CreditAccountOperation[];
  constructor(p: LegacyApiOperation) {
    super(p, p.operation);
    const inner = (p.multicall ?? []) as LegacyApiOperation[];
    this.multicall = inner.map(o =>
      CreditSessionOperationParser.deserialize(injectMissing(o, p)),
    );
  }
  getDetails(): [OperationDetails, CreditAccountOperation[]] {
    return [{ from: [accountDetails("" as Address)] }, this.multicall];
  }
}

class OpenCreditAccountEvent extends CreditAccountOperation {
  readonly multicall: CreditAccountOperation[];
  constructor(p: LegacyApiOperation) {
    super(p, p.operation);
    const inner = (p.multicall ?? []) as LegacyApiOperation[];
    this.multicall = inner.map(o =>
      CreditSessionOperationParser.deserialize(injectMissing(o, p)),
    );
  }
  getDetails(): [OperationDetails, CreditAccountOperation[]] {
    return [{ from: [accountDetails("" as Address)] }, this.multicall];
  }
}

class CloseCreditAccountWithMulticall extends CreditAccountOperation {
  readonly multicall: CreditAccountOperation[];
  readonly remainingFunds: bigint;
  constructor(p: LegacyApiOperation) {
    super(p, p.operation);
    const inner = (p.multicall ?? []) as LegacyApiOperation[];
    this.multicall = inner.map(o =>
      CreditSessionOperationParser.deserialize(injectMissing(o, p)),
    );
    this.remainingFunds = toBigInt(p.remainingFunds as BigNumberish);
  }
  getDetails(): [OperationDetails, CreditAccountOperation[]] {
    return [{ from: [accountDetails("" as Address)] }, this.multicall];
  }
}

class LiquidateCreditAccountEvent extends CreditAccountOperation {
  readonly multicall: CreditAccountOperation[];
  readonly remainingFunds: bigint;
  readonly to?: Address;
  constructor(p: LegacyApiOperation) {
    super(p, p.operation);
    const inner = (p.multicall ?? []) as LegacyApiOperation[];
    this.multicall = inner.map(o =>
      CreditSessionOperationParser.deserialize(injectMissing(o, p)),
    );
    this.remainingFunds = toBigInt(p.remainingFunds as BigNumberish);
    this.to = p.to as Address | undefined;
  }
  getDetails(): [OperationDetails, CreditAccountOperation[]] {
    return [{ from: [accountDetails("" as Address)] }, this.multicall];
  }
}

class RewardsEvent extends CreditAccountOperation {
  readonly assets: { token: Address; amount: bigint }[];
  constructor(p: LegacyApiOperation) {
    super(p, p.operation);
    const rewards = ((p.rewards as any[]) ?? []).map(r => ({
      token: (r.token as Address).toLowerCase() as Address,
      amount: toBigInt(r.amount),
    }));
    this.assets = rewards;
  }
  getDetails(_: Address, tokens: TokensList): [OperationDetails] {
    return [
      {
        from: this.assets.map(a =>
          tokenAmountDetails(a.token, a.amount, tokens),
        ),
      },
    ];
  }
}

class ConvexStakeEvent extends CreditAccountOperation {
  readonly token: Address;
  readonly amount: bigint;
  constructor(p: LegacyApiOperation) {
    super(p, p.operation);
    const st = p.stakedToken as { token: Address; amount: BigNumberish };
    this.token = (st.token as Address).toLowerCase() as Address;
    this.amount = toBigInt(st.amount);
  }
  getDetails(_: Address, tokens: TokensList): [OperationDetails] {
    return [{ from: [tokenAmountDetails(this.token, this.amount, tokens)] }];
  }
}

class CurveAddLiquidityEvent extends CreditAccountOperation {
  readonly added: { token: Address; amount: bigint }[];
  readonly lpToken: Address;
  readonly lpAmount: bigint;
  constructor(p: LegacyApiOperation) {
    super(p, p.operation);
    this.added = ((p.addedLiquidity as any[]) ?? []).map(r => ({
      token: (r.token as Address).toLowerCase() as Address,
      amount: toBigInt(r.amount),
    }));
    this.lpToken = (p.lpToken as Address).toLowerCase() as Address;
    this.lpAmount = toBigInt(p.lpAmount as BigNumberish);
  }
  getDetails(_: Address, tokens: TokensList): [OperationDetails] {
    return [
      {
        from: this.added.map(a =>
          tokenAmountDetails(a.token, a.amount, tokens),
        ),
        to: [tokenAmountDetails(this.lpToken, this.lpAmount, tokens)],
      },
    ];
  }
}

class CurveRemoveLiquidityEvent extends CreditAccountOperation {
  readonly received: { token: Address; amount: bigint }[];
  readonly lpToken: Address;
  readonly lpAmount: bigint;
  constructor(p: LegacyApiOperation) {
    super(p, p.operation);
    this.received = ((p.tokenReceived as any[]) ?? []).map(r => ({
      token: (r.token as Address).toLowerCase() as Address,
      amount: toBigInt(r.amount),
    }));
    this.lpToken = (p.lpToken as Address).toLowerCase() as Address;
    this.lpAmount = toBigInt(p.lpAmount as BigNumberish);
  }
  getDetails(_: Address, tokens: TokensList): [OperationDetails] {
    return [
      {
        from: [tokenAmountDetails(this.lpToken, this.lpAmount, tokens)],
        to: this.received.map(a =>
          tokenAmountDetails(a.token, a.amount, tokens),
        ),
      },
    ];
  }
}

class ConvexDepositAndStakeEvent extends CreditAccountOperation {
  readonly token: Address;
  readonly amount: bigint;
  constructor(p: LegacyApiOperation) {
    super(p, p.operation);
    this.token = (p.depositToken as Address).toLowerCase() as Address;
    this.amount = toBigInt(p.depositAmount as BigNumberish);
  }
  getDetails(_: Address, tokens: TokensList): [OperationDetails] {
    return [{ from: [tokenAmountDetails(this.token, this.amount, tokens)] }];
  }
}

class ConvexWithdrawEvent extends CreditAccountOperation {
  readonly token: Address;
  readonly amount: bigint;
  constructor(p: LegacyApiOperation) {
    super(p, p.operation);
    this.token = (p.withdrawToken as Address).toLowerCase() as Address;
    this.amount = toBigInt(p.withdrawAmount as BigNumberish);
  }
  getDetails(_: Address, tokens: TokensList): [OperationDetails] {
    return [{ from: [tokenAmountDetails(this.token, this.amount, tokens)] }];
  }
}

class GenericEvent extends CreditAccountOperation {
  readonly raw: LegacyApiOperation;
  constructor(p: LegacyApiOperation) {
    super(p, p.operation);
    this.raw = p;
  }
  getDetails(): [OperationDetails] {
    return [
      {
        from: [
          {
            description: JSON.stringify(
              this.raw,
              (_, v) => (typeof v === "bigint" ? v.toString() : v),
              0,
            ).slice(0, 200),
          },
        ],
      },
    ];
  }
}

/**
 * Inner operations emitted from {@link toLegacyOperations} reuse the parent's
 * tx/block/timestamp/sessionId via spread, but for safety we make sure the
 * required fields are present when the inner payload didn't carry them.
 */
function injectMissing(
  inner: LegacyApiOperation,
  parent: LegacyApiOperation,
): LegacyApiOperation {
  const out: LegacyApiOperation = { ...inner };
  out.txHash ??= parent.txHash;
  out.blockNum ??= parent.blockNum;
  out.timestamp ??= parent.timestamp;
  out.sessionId ??= parent.sessionId;
  out.protocol ??= parent.protocol;
  return out;
}

export class CreditSessionOperationParser {
  static deserialize(p: LegacyApiOperation): CreditAccountOperation {
    switch (p.operation) {
      case "OpenCreditAccount":
        return new OpenCreditAccountEvent(p);
      case "CloseCreditAccount":
        return new CloseCreditAccountWithMulticall(p);
      case "LiquidateCreditAccount":
        return new LiquidateCreditAccountEvent(p);
      case "PartiallyLiquidateCreditAccount":
        return new PartialLiquidateEvent(p);

      case "MultiCall":
      case "BotMulticall":
        return new MultiCallEvent(p);

      case "IncreaseBorrowedAmount":
      case "DecreaseBorrowedAmount":
      case "IncreaseDebt":
      case "DecreaseDebt":
        return new ChangeBorrowedAmount(p);

      case "AddCollateral":
      case "MakerDeposit":
      case "YearnDeposit":
      case "YearnWithDrawn":
        return new AmountEvent(p);

      case "WithdrawCollateral":
        return new WithdrawCollateralEvent(p);

      case "UpdateQuota":
        return new UpdateQuotaEvent(p);

      case "DirectTokenTransfer":
        return new DirectTokenTransferEvent(p);

      case "Swap":
      case "UniswapSwap":
      case "BalancerSwap":
      case "CurveExchange":
      case "CurveRemoveLiquidityOneCoin":
      case "CurveWithdrawal":
      case "ConvexDeposit":
      case "WstETHWrap":
      case "WstETHUnwrap":
      case "LidoSubmit":
      case "VaultDeposit":
      case "MakerRedeem":
      case "Deposit":
      case "Withdraw":
        return new TransactionEvent(p);

      case "CurveAddLiquidity":
        return new CurveAddLiquidityEvent(p);
      case "CurveRemoveLiquidity":
        return new CurveRemoveLiquidityEvent(p);
      case "CurveClaims":
      case "GetReward":
      case "RewardClaimed":
      case "ConvexWithdrawAndClaim":
        return new RewardsEvent(p);
      case "ConvexStake":
        return new ConvexStakeEvent(p);
      case "ConvexDepositAndStake":
        return new ConvexDepositAndStakeEvent(p);
      case "ConvexWithdraw":
        return new ConvexWithdrawEvent(p);

      default:
        return new GenericEvent(p);
    }
  }

  static deserializeArray(
    list: LegacyApiOperation[],
  ): CreditAccountOperation[] {
    return list.map(o => CreditSessionOperationParser.deserialize(o));
  }
}

export const isMulticallOperation = (op: unknown): boolean =>
  typeof op === "object" && !!op && Array.isArray((op as any).multicall);

// Re-export so consumers can format leverage values from the SDK.
export { LEVERAGE_DECIMALS };
